import React, { Fragment } from 'react';
import { FileCogIcon, BarChart3Icon, LightbulbIcon, BellIcon, SettingsIcon, ArrowRightIcon, DropletIcon, TrendingUpIcon, MessageSquareTextIcon, ChevronRightIcon, CreditCardIcon } from 'lucide-react';
import { solutionStages, stageExtras, type Bi, type StageKey } from '../../../data/waterSolution';
import { SOURCE_ICONS } from './SolutionCards';
type P = {
  isFR: boolean;
};
const STAGE_ICONS: Record<StageKey, typeof BellIcon> = {
  capture: FileCogIcon,
  display: BarChart3Icon,
  understand: LightbulbIcon,
  alert: BellIcon,
  act: SettingsIcon
};
const TONE = {
  red: '#E5484D',
  orange: '#EA7A2C',
  indigo: '#4F46E5'
} as const;
const MINI = 'rounded-xl border border-[#EEEEF4] bg-white p-3.5 h-[92px]';
function Extra({
  k,
  isFR
}: P & {
  k: StageKey;
}) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  if (k === 'capture') return <ul className={`${MINI} grid grid-cols-3 divide-x divide-[#F0F0F6]`} data-id="element-7426" data-mp="rea687kydfe7">
        {stageExtras.capture.map(c => {
      const Icon = SOURCE_ICONS[c.key];
      return <li key={c.key} className="flex flex-col items-center justify-center gap-1.5" data-id="element-7427" data-mp="s5jhp056ecz2">
              <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-7428" data-mp="w6v837vvj28b" />
              <span className="font-sans text-[11px] text-[#5B6478] whitespace-nowrap" data-id="element-7429" data-mp="i8y801i7odow">{pick(c.label)}</span>
            </li>;
    })}
      </ul>;
  if (k === 'display') return <ul className={`${MINI} flex items-end gap-2 px-5`} aria-hidden="true" data-id="element-7430" data-mp="8i3ov609hu7o">
        {stageExtras.displayBars.map((b, i) => <li key={i} className="flex-1 rounded-t-[3px]" style={{
      height: `${b}%`,
      background: i === stageExtras.displayBars.length - 1 ? '#4F46E5' : '#DCDDFB'
    }} data-id="element-7431" data-mp="3vo2x322r9ocg" data-mpi={i} />)}
      </ul>;
  if (k === 'understand') return <ul className={`${MINI} space-y-1.5 py-3`} data-id="element-7432" data-mp="3lpnanceob39a">
        {stageExtras.understand.map(u => {
      const Icon = u.key === 'unusual' ? TrendingUpIcon : DropletIcon;
      return <li key={u.key} className="flex items-center gap-2 font-sans text-[12.5px]" style={{
        color: TONE[u.tone]
      }} data-id="element-7433" data-mp="37kmwhc4vwchr">
              <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2.2} aria-hidden="true" data-id="element-7434" data-mp="32swk70mdzenp" />
              {pick(u.label)}
            </li>;
    })}
      </ul>;
  if (k === 'alert') return <div className={`${MINI} flex items-center gap-3`} data-id="element-7435" data-mp="2sx1wvs77wvel">
        <MessageSquareTextIcon className="w-6 h-6 text-[#4F46E5] shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-7436" data-mp="3qjsfw8tyqu08" />
        <span className="flex-1 min-w-0" data-id="element-7437" data-mp="3ll7ggbmjcecu">
          <span className="block font-sans text-[12.5px] font-semibold text-[#121A3A]" data-id="element-7438" data-mp="3li32wnn6k69i">{pick(stageExtras.alert.title)}</span>
          <span className="block font-sans text-[11px] text-[#5B6478] leading-snug" data-id="element-7439" data-mp="3bm8hjkrknx31">{pick(stageExtras.alert.text)}</span>
        </span>
        <ChevronRightIcon className="w-3.5 h-3.5 text-[#8A90A2] shrink-0" aria-hidden="true" data-id="element-7440" data-mp="256z397nnnhdv" />
      </div>;
  return <div className={`${MINI} flex items-start gap-3`} data-id="element-7441" data-mp="2059vc0vqbju0">
      <span className="w-9 h-9 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7442" data-mp="fpamh59u9ui9">
        <CreditCardIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-7443" data-mp="kowzpepo3ar2" />
      </span>
      <span className="flex-1 min-w-0" data-id="element-7444" data-mp="pmoow0yxgirk">
        <span className="block font-sans text-[12px] font-semibold text-[#121A3A]" data-id="element-7445" data-mp="plnc9s29jo7q">{pick(stageExtras.act.title)}</span>
        <span className="block font-sans text-[12px] text-[#121A3A]" data-id="element-7446" data-mp="ufhtmouebr3n">{pick(stageExtras.act.amount)}</span>
        <span className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-[#4F46E5] px-3 py-1 font-sans text-[10.5px] font-medium text-white whitespace-nowrap" data-id="element-7447" data-mp="13ed1s6h03b9l">
          {pick(stageExtras.act.cta)}
          <ArrowRightIcon className="w-3 h-3" aria-hidden="true" data-id="element-7448" data-mp="1dcapmsuy761o" />
        </span>
      </span>
    </div>;
}
export function SolutionStages({
  isFR
}: P) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <ol className="max-w-[1640px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr] gap-x-4 gap-y-12" data-id="element-7449" data-mp="3f3e2zpw82ypx">
      {solutionStages.map((s, i) => {
      const Icon = STAGE_ICONS[s.key];
      return <Fragment key={s.key} data-id="element-7450" data-mp="14iwnzbwirn55" data-mpi={i}>
            <li className="flex flex-col" data-id="element-7451" data-mp="1igtky9evxvkc">
              <div className="flex items-start gap-3" data-id="element-7452" data-mp="1ifijig8wwzwe">
                <span className="relative shrink-0" data-id="element-7453" data-mp="1eegj3w39ehsu">
                  <span className="absolute -top-2 -left-3 w-9 h-9 rounded-full bg-[#F1F0FD] flex items-center justify-center font-sans text-[13px] font-semibold text-[#4F46E5]" data-id="element-7454" data-mp="1ecluo7ss47k0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="mt-4 ml-5 w-[62px] h-[62px] rounded-full bg-[#F1F0FD] flex items-center justify-center" aria-hidden="true" data-id="element-7455" data-mp="zihckpvssppt">
                    <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} data-id="element-7456" data-mp="uomola95tokx" />
                  </span>
                </span>
                <span className="min-w-0 pt-0.5" data-id="element-7457" data-mp="2zt593ngnv74">
                  <span className="block font-sans text-[15px] font-bold uppercase tracking-[0.1em] text-[#3F37C9]" data-id="element-7458" data-mp="2z1kyozbdjw5">{pick(s.label)}</span>
                  <span className="block font-sans text-[13px] leading-[1.5] text-[#5B6478] mt-1.5" data-id="element-7459" data-mp="3trqtkzftu96p">{pick(s.text)}</span>
                </span>
              </div>
              <div className="mt-auto pt-4" data-id="element-7460" data-mp="3f4ntcl04dmfo">
                <Extra k={s.key} isFR={isFR} data-id="element-7461" data-mp="3k1nx2p9hyd3m" />
              </div>
            </li>
            {i < solutionStages.length - 1 && <li className="hidden 2xl:flex items-center pt-10 self-start" aria-hidden="true" data-id="element-7462" data-mp="bnfdu7sv6yo4">
                <ArrowRightIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.8} data-id="element-7463" data-mp="gk5pmixmz83q" />
              </li>}
          </Fragment>;
    })}
    </ol>;
}